import { BreakpointObserver } from '@angular/cdk/layout'
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  output,
  runInInjectionContext,
  ViewChild,
  viewChild
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { map } from 'rxjs'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ComponentWithDataSetter } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TimeRange, TimeRangePickerContentComponent } from './time-range-picker-content/time-range-picker-content.component'

@Component({
  selector: 'app-time-range-picker',
  standalone: true,
  imports: [DropdownComponent, TimeRangePickerContentComponent, ModalComponent],
  templateUrl: './time-range-picker.component.html',
  styleUrl: './time-range-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeRangePickerComponent {
  public readonly startTime = input<string | null>(null)
  public readonly endTime = input<string | null>(null)
  public readonly useModalOnMobile = input<boolean>(false)
  public readonly mobileModalTitle = input<string>('Set event timings')
  public readonly rangeSelected = output<TimeRange>()

  @ViewChild('dropdownRef') public dropdownRef!: DropdownComponent
  private readonly _modalPickerContent = viewChild<TimeRangePickerContentComponent>('modalPickerContent')
  private readonly _injector: Injector = inject(Injector)
  private readonly _breakpointObserver: BreakpointObserver = inject(BreakpointObserver)
  private readonly _modalService: ModalService = inject(ModalService)
  private readonly _destroyRef: DestroyRef = inject(DestroyRef)
  public readonly mobileModalId: string =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? `time-range-picker-mobile-${crypto.randomUUID()}`
      : `time-range-picker-mobile-${Math.random().toString(36).slice(2, 11)}`
  public readonly mobileModalHeaderIcon: string = `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 0C7.21997 0 5.47991 0.527841 3.99987 1.51677C2.51983 2.50571 1.36628 3.91131 0.685088 5.55585C0.00389956 7.20038 -0.17433 9.00998 0.172937 10.7558C0.520203 12.5016 1.37737 14.1053 2.63604 15.364C3.89472 16.6226 5.49836 17.4798 7.24419 17.8271C8.99002 18.1743 10.7996 17.9961 12.4442 17.3149C14.0887 16.6337 15.4943 15.4802 16.4832 14.0001C17.4722 12.5201 18 10.78 18 9C17.9974 6.61385 17.0483 4.32619 15.3611 2.63893C13.6738 0.951673 11.3861 0.00262044 9 0ZM12.5838 12.5838C12.415 12.7525 12.1861 12.8473 11.9475 12.8473C11.7089 12.8473 11.48 12.7525 11.3112 12.5838L8.3646 9.6372C8.19625 9.46772 8.10123 9.23888 8.1 9V5.4C8.1 5.1613 8.19482 4.93239 8.36361 4.7636C8.53239 4.59482 8.76131 4.5 9 4.5C9.2387 4.5 9.46762 4.59482 9.6364 4.7636C9.80518 4.93239 9.9 5.1613 9.9 5.4V8.6274L12.5838 11.3112C12.7525 11.48 12.8473 11.7088 12.8473 11.9475C12.8473 12.1861 12.7525 12.415 12.5838 12.5838Z" fill="#111928"/></svg>`
  public readonly isMobileLayout = toSignal(this._breakpointObserver.observe('(max-width: 768px)').pipe(map((r) => r.matches)), {
    initialValue: typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  })
  public readonly isMobileModalPathActive = computed<boolean>(() => Boolean(this.useModalOnMobile()) && Boolean(this.isMobileLayout()))
  public readonly displayLabel = computed<string>(() => {
    const start: string | null = this.startTime()
    const end: string | null = this.endTime()
    if (!start || !end) return 'None'
    return `${start} - ${end}`
  })
  private _mobilePickerModalWasOpen: boolean = false

  constructor() {
    effect(() => {
      this._listenToModalState()
    })

    effect(() => {
      if (!this.isMobileLayout() && this._modalService.isModalOpen(this.mobileModalId)) {
        this._modalService.toggleModal(this.mobileModalId)
      }
    })
  }

  /**
   * @description Listen to modal state changes.
   */
  private _listenToModalState(): void {
    this._modalService.modalState$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((state) => {
      const open: boolean = Boolean(state[this.mobileModalId])
      if (open) {
        if (!this._mobilePickerModalWasOpen) {
          this._mobilePickerModalWasOpen = true
          runInInjectionContext(this._injector, () => {
            afterNextRender(() => {
              const inst: TimeRangePickerContentComponent | undefined = this._modalPickerContent()
              if (!inst) return
              this._wireContentInstance(inst)
              inst.init()
            })
          })
        }
      } else {
        this._mobilePickerModalWasOpen = false
      }
    })
  }

  /**
   * @description Open time-range picker modal on mobile viewports.
   * @param event - Optional click event to stop propagation.
   */
  public openMobileModal(event?: Event): void {
    event?.stopPropagation()
    event?.preventDefault()
    if (!this._modalService.isModalOpen(this.mobileModalId)) {
      this._modalService.toggleModal(this.mobileModalId)
    }
  }

  /**
   * @description Close time-range picker modal if open.
   */
  private _closeMobilePickerModal(): void {
    if (this._modalService.isModalOpen(this.mobileModalId)) {
      this._modalService.toggleModal(this.mobileModalId)
    }
  }

  /**
   * @description Close currently active picker UI (modal on mobile, dropdown on desktop).
   */
  private _closePickerUi(): void {
    if (this.isMobileModalPathActive()) {
      this._closeMobilePickerModal()
    } else {
      this.dropdownRef?.close()
    }
  }

  /**
   * @description Wire picker content instance with inputs and handlers.
   * @param instance - Content component instance created inside dropdown or modal.
   */
  private _wireContentInstance(instance: TimeRangePickerContentComponent): void {
    instance.initialStartTime = (this.startTime() || '00:00').slice(0, 5)
    instance.initialEndTime = (this.endTime() || '00:00').slice(0, 5)
    instance.onApply = (range: TimeRange) => {
      this.rangeSelected.emit(range)
      this._closePickerUi()
    }
    instance.closeDropdown = () => this._closePickerUi()
  }

  /**
   * @description Config object for shared dropdown to create and initialize picker content.
   * @returns Dropdown content config with `dataSetter`.
   */
  public readonly contentSetter = computed<ComponentWithDataSetter<TimeRangePickerContentComponent>>(() => {
    return {
      component: TimeRangePickerContentComponent,
      dataSetter: (instance: TimeRangePickerContentComponent) => {
        this._wireContentInstance(instance)
        instance.init()
      }
    }
  })
}
